import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    SxProps,
    DialogActions,
    DialogContent,
    TextField,
    InputAdornment,
    IconButton,
    Stack,
    Box,
    DialogTitle,
    Divider,
} from '@mui/material';
import Logo from '../../../Logo/Logo';
import { bodyFont, vars } from '../../../../../Theme';
import Icon from '@mdi/react';
import { mdiEyeOutline, mdiEyeOffOutline, mdiAlertCircle, mdiKeyChange } from '@mdi/js';
import CtaButton from '../../_parts/CtaButton';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import Message from '../../_parts/Message';
import {
    updateUserApprovalPassword,
    resetWorkflowPasswordError,
} from '../../../../../redux/user/sliceUserProfile';
import dayjs from 'dayjs';
import ResetWorkflowPasswordDialog from '../ResetWorkflowPasswordDialog/ResetWorkflowPasswordDialog';

/** Styles for Dialog's component. */
const dialogSx: SxProps = {
    '& .MuiDialog-paper': {
        width: '90%',
        minWidth: '350px',
        maxWidth: '500px',
        maxHeight: '560px',
        paddingBottom: '8px',
    },
};

const passwordInputDiscSx: SxProps = {
    marginTop: '16px',

    '& .MuiInputBase-input': {
        WebkitTextSecurity: 'disc',
    },
};

let newDate: string;

/**
 * Interface that describes WorkflowApprovalPasswordDialog's props.
 */
interface IWorkflowApprovalPasswordDialogProps extends React.PropsWithChildren {
    open: boolean;
    onClose?: () => void;
    onPasswordDateChange: (newDate: string) => void;
}

export default ({ open, onClose, onPasswordDateChange }: IWorkflowApprovalPasswordDialogProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'workflowPasswordDialog' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    const validatePassword = useSelector(
        (_store: IAppReduxStore) => _store.userProfile.validatePassword.data,
    );
    const validatePasswordLoading = useSelector(
        (_store: IAppReduxStore) => _store.userProfile.validatePassword.loading,
    );
    const validatePasswordError = useSelector(
        (_store: IAppReduxStore) => _store.userProfile.validatePassword.error,
    );
    const resetWfPasswordSuccess = useSelector(
        (_store: IAppReduxStore) => _store.userProfile.resetPassword.data,
    );

    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
    const [showError, setShowError] = useState(false);
    const [newPasswordsError, setNewPasswordsError] = useState(false);
    const [resetWorkflowPasswordDialogOpen, setResetWorkflowPasswordDialogOpen] = useState(false);

    useEffect(() => {
        if (validatePasswordError) {
            setShowError(true);
        }
    }, [validatePasswordError]);

    useEffect(() => {
        if (validatePassword) {
            onPasswordDateChange(newDate);
        }
    }, [validatePassword]);

    /** Shows success message for the reset of the approval password. */
    useEffect(() => {
        if (resetWfPasswordSuccess) {
            setResetWorkflowPasswordDialogOpen(false);
        }
    }, [resetWfPasswordSuccess]);

    const handleShowOldPassword = () => {
        setShowOldPassword(!showOldPassword);
    };
    const handleShowNewPassword = () => {
        setShowNewPassword(!showNewPassword);
    };
    const handleShowConfirmNewPassword = () => {
        setShowConfirmNewPassword(!showConfirmNewPassword);
    };

    const handleResetPasswordDialog = () => {
        setResetWorkflowPasswordDialogOpen(!resetWorkflowPasswordDialogOpen);
    };

    const handleConfirm = () => {
        if (newPassword !== confirmNewPassword) {
            setShowError(true);
            return;
        }

        const isoCurrentDateTime = new Date().toISOString();
        newDate = dayjs(isoCurrentDateTime).format('DD.MM.YYYY');

        dispatch(
            updateUserApprovalPassword({
                oldPassword: oldPassword,
                newPassword: confirmNewPassword,
                timestamp: newDate,
            }),
        );
    };

    const handleClose = () => {
        setShowOldPassword(false);
        setShowNewPassword(false);
        setShowConfirmNewPassword(false);
        setShowError(false);
        dispatch(resetWorkflowPasswordError());
        onClose();
    };

    return (
        <>
            <Dialog open={open} sx={dialogSx} onClose={handleClose}>
                <Stack direction='row' sx={{ margin: '24px 24px 0px 24px' }}>
                    <Logo darkMode boxSx={{ pl: 0 }} />
                </Stack>

                <DialogContent sx={{ px: 0, overflowX: 'hidden', pt: 3, marginX: '24px' }}>
                    {showError && (
                        <Message
                            iconWrapperSx={{ display: 'flex' }}
                            wrapperSx={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px 12px 12px 16px',
                            }}
                            text={t('invalidPassword')}
                            color={vars.errorDialogRed}
                            icon={mdiAlertCircle}
                        />
                    )}

                    <Box>
                        <DialogTitle
                            sx={{
                                px: 0,
                                pt: 0,
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'center',
                            }}
                        >
                            <Icon path={mdiKeyChange} size={1} />
                            {t('changeApprovalPasswordTitle')}
                        </DialogTitle>
                        <TextField
                            disabled={validatePasswordLoading}
                            autoFocus
                            size='small'
                            error={showError}
                            color={showError ? 'error' : 'primary'}
                            label={t('oldApprovalPassword')}
                            type={'text'}
                            fullWidth
                            value={oldPassword}
                            onChange={event => setOldPassword(event.target.value)}
                            onKeyUp={e => e.key === 'Enter' && handleConfirm()}
                            sx={showOldPassword ? { marginTop: '16px' } : passwordInputDiscSx}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            {oldPassword !== '' && (
                                                <IconButton
                                                    onClick={handleShowOldPassword}
                                                    edge='end'
                                                >
                                                    {showOldPassword ? (
                                                        <Icon path={mdiEyeOutline} size={1} />
                                                    ) : (
                                                        <Icon path={mdiEyeOffOutline} size={1} />
                                                    )}
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            disabled={validatePasswordLoading}
                            size='small'
                            error={showError}
                            color={showError ? 'error' : 'primary'}
                            label={t('newApprovalPassword')}
                            type={'text'}
                            fullWidth
                            value={newPassword}
                            onChange={event => {
                                setNewPassword(event.target.value);

                                if (newPassword && confirmNewPassword) {
                                    setNewPasswordsError(event.target.value !== confirmNewPassword);
                                }
                            }}
                            onKeyUp={e => e.key === 'Enter' && handleConfirm()}
                            sx={
                                showNewPassword
                                    ? { marginTop: '32px' }
                                    : { ...passwordInputDiscSx, marginTop: '32px' }
                            }
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            {newPassword !== '' && (
                                                <IconButton
                                                    onClick={handleShowNewPassword}
                                                    edge='end'
                                                >
                                                    {showNewPassword ? (
                                                        <Icon path={mdiEyeOutline} size={1} />
                                                    ) : (
                                                        <Icon path={mdiEyeOffOutline} size={1} />
                                                    )}
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            disabled={validatePasswordLoading}
                            size='small'
                            error={showError || newPasswordsError}
                            color={showError || newPasswordsError ? 'error' : 'primary'}
                            label={t('confirmNewApprovalPassword')}
                            type={'text'}
                            fullWidth
                            value={confirmNewPassword}
                            helperText={
                                newPasswordsError ? (
                                    <Message
                                        text={t('mismatchedPassword')}
                                        icon={mdiAlertCircle}
                                        iconSize={0.75}
                                        color={vars.errorDialogRed}
                                        typographySx={{
                                            fontWeight: 400,
                                            fontSize: '12px',
                                            ...bodyFont,
                                        }}
                                        wrapperSx={{
                                            border: 'none',
                                            p: 0,
                                            m: 0,
                                            gap: 0.5,
                                            height: '20px',
                                        }}
                                    />
                                ) : (
                                    ' '
                                )
                            }
                            onChange={event => {
                                setConfirmNewPassword(event.target.value);

                                if (newPassword && confirmNewPassword) {
                                    setNewPasswordsError(event.target.value !== newPassword);
                                }
                            }}
                            onKeyUp={e => e.key === 'Enter' && handleConfirm()}
                            sx={
                                showConfirmNewPassword
                                    ? {
                                          marginTop: '32px',
                                          '& .MuiFormHelperText-root': {
                                              marginLeft: 'unset',
                                          },
                                      }
                                    : {
                                          ...passwordInputDiscSx,
                                          marginTop: '32px',
                                          '& .MuiFormHelperText-root': {
                                              marginLeft: 'unset',
                                          },
                                      }
                            }
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position='end'>
                                            {confirmNewPassword !== '' && (
                                                <IconButton
                                                    onClick={handleShowConfirmNewPassword}
                                                    edge='end'
                                                >
                                                    {showConfirmNewPassword ? (
                                                        <Icon path={mdiEyeOutline} size={1} />
                                                    ) : (
                                                        <Icon path={mdiEyeOffOutline} size={1} />
                                                    )}
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </Box>
                </DialogContent>

                <Divider sx={{ mb: 1, width: '100%' }} />

                <DialogActions sx={{ p: 0, marginX: '24px' }}>
                    <CtaButton
                        variant='contained'
                        text={t('resetPassword')}
                        disabled={validatePasswordLoading}
                        onClick={handleResetPasswordDialog}
                        sx={{
                            mr: 'auto',
                            backgroundColor: vars.errorDialogRed,
                            '&:hover': { backgroundColor: vars.brandPink },
                        }}
                    />
                    <CtaButton
                        variant='text'
                        text={t('cancel')}
                        disabled={validatePasswordLoading}
                        onClick={handleClose}
                    />
                    <CtaButton
                        variant='contained'
                        disabled={
                            !oldPassword ||
                            !newPassword ||
                            !confirmNewPassword ||
                            validatePasswordLoading ||
                            newPasswordsError
                        }
                        text={t('update')}
                        onClick={handleConfirm}
                    />
                </DialogActions>
            </Dialog>
            {resetWorkflowPasswordDialogOpen && (
                <ResetWorkflowPasswordDialog
                    open={resetWorkflowPasswordDialogOpen}
                    handleClose={handleResetPasswordDialog}
                />
            )}
        </>
    );
};
